import { CommentRepository } from "@amityco/ts-sdk";
import {
  TextareaHTMLAttributes,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";

import { Button, ColumnModal } from "@/components";
import { Image02 } from "@/icons";
import { useTranslate } from "@/providers/context";
import { cn } from "@/utils";

import { AttachmentPreview, CommunityPostHeader } from "../..";
import {
  ACCEPTED_IMAGE_FILE_TYPES,
  MAX_ATTACHMENTS,
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_LABEL,
} from "../../../const";
import { CommentItem } from "../../../types";
import { uploadMedia } from "../../../util";
import { useCommunityError } from "../../../hooks/useCommunityError";

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  className?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ className, ...props }: TextAreaProps, ref) => (
    <textarea
      ref={ref}
      className={cn(
        "p2-regular text-item-primary transition-colors duration-100 placeholder:text-item-secondary",
        "outline-none disabled:bg-transparent",
        className,
      )}
      {...props}
    />
  ),
);

TextArea.displayName = "TextArea";

const useAutoResize = (value: string | undefined) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    textarea.style.height = "93px";
    textarea.style.height = `${Math.max(93, textarea.scrollHeight)}px`;
  }, [value]);

  return textareaRef;
};

const EditCommentInput = ({
  comment,
  onSuccess,
  onCancel,
}: {
  comment: CommentItem;
  onSuccess: () => void;
  onCancel: () => void;
}) => {
  const { translate } = useTranslate();
  const {
    handlePostingError,
    textContainsBlockedContent,
    showBlockedContentToast,
  } = useCommunityError();

  const [value, setValue] = useState<string | undefined>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [pendingAttachment, setPendingAttachment] = useState<File | null>(null);
  const [attachments, setAttachments] = useState<Amity.File<"image">[]>([]);

  const textareaRef = useAutoResize(value);

  const isLoading = useMemo(
    () => isSubmitting || !!pendingAttachment || !!uploadProgress,
    [isSubmitting, pendingAttachment, uploadProgress],
  );

  const saveIsDisabled = useMemo(() => {
    if (isLoading || !value) {
      return true;
    }

    const textEdited = comment.textContent !== value;
    const commentAttachmentIds = attachments.map((a) => a.fileId);
    const attachmentIds = comment.attachments?.map((a) => a.fileId);
    const attachmentsEdited =
      JSON.stringify(commentAttachmentIds) !== JSON.stringify(attachmentIds);

    if (!textEdited && !attachmentsEdited) {
      return true;
    }

    return false;
  }, [isLoading, value, attachments, comment.attachments, comment.textContent]);

  useEffect(() => {
    setValue(comment.textContent);
    setAttachments(comment.attachments ?? []);
  }, [comment]);

  const handleAddAttachment = useCallback(
    async (file: File) => {
      if (!ACCEPTED_IMAGE_FILE_TYPES.includes(file.type)) {
        toast(
          translate("Invalid file type. Please select an image or video file."),
        );
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast(
          `${translate("File size must be less than")} ${MAX_FILE_SIZE_LABEL}`,
        );
        return;
      }

      setUploadProgress(0);
      setPendingAttachment(file);

      try {
        const uploadedFile = await uploadMedia(file, setUploadProgress);
        setAttachments([...attachments, uploadedFile as Amity.File<"image">]);
      } catch (error) {
        toast(translate("File upload failed. Please try again."));
      } finally {
        setPendingAttachment(null);
        setUploadProgress(null);
      }
    },
    [
      attachments,
      setAttachments,
      setUploadProgress,
      setPendingAttachment,
      translate,
    ],
  );

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);

    if (!value) {
      setIsSubmitting(false);
      return;
    }

    if (textContainsBlockedContent(value)) {
      showBlockedContentToast();
      setIsSubmitting(false);
      return;
    }

    try {
      await CommentRepository.updateComment(comment.id, {
        data: {
          text: value,
          attachment: attachments.map(({ fileId }) => fileId),
        },
      }).catch((e) => handlePostingError(e, true));

      setValue("");
      onSuccess();
    } catch (e) {
      console.error(e);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    comment.id,
    value,
    attachments,
    handlePostingError,
    setValue,
    setIsSubmitting,
    onSuccess,
    textContainsBlockedContent,
    showBlockedContentToast,
  ]);

  return (
    <div className="flex w-full flex-col">
      <TextArea
        ref={textareaRef}
        className="max-h-[calc(85vh-302px)] min-h-[93px] w-full resize-none"
        onChange={(e) => {
          setValue(e.target.value);
        }}
        value={value}
        placeholder={translate("What's on your mind?")}
        disabled={isSubmitting}
      />
      <div className="mt-8 flex h-[100px] w-fit max-w-full gap-2 overflow-x-auto">
        {attachments.map((file) => (
          <AttachmentPreview
            key={file.fileId}
            file={file}
            onRemove={() =>
              setAttachments(
                attachments.filter(({ fileId }) => fileId !== file.fileId),
              )
            }
          />
        ))}
        {pendingAttachment && (
          <AttachmentPreview
            pending={{
              progress: uploadProgress ?? 0,
              file: pendingAttachment,
            }}
          />
        )}
      </div>
      <div className="flex w-full items-center justify-between pb-6 pt-4">
        <Button
          size="s"
          variant="tertiary"
          icon={<Image02 />}
          className="h-fit rounded-md p-[2px]"
          disabled={isLoading || attachments.length >= MAX_ATTACHMENTS}
          onClick={() => {
            const fileInput = document.createElement("input");
            fileInput.type = "file";
            fileInput.accept = ACCEPTED_IMAGE_FILE_TYPES.join(", ");
            fileInput.onchange = (event) => {
              const file = (event.target as HTMLInputElement).files?.[0];
              if (file) {
                handleAddAttachment(file);
              }
            };
            fileInput.click();
          }}
        />
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
            {translate("Cancel")}
          </Button>
          <Button
            disabled={saveIsDisabled}
            loading={isSubmitting}
            onClick={handleSubmit}
          >
            {translate("Save")}
          </Button>
        </div>
      </div>
    </div>
  );
};

interface CommunityCommentModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  comment?: CommentItem;
}

export const CommunityCommentModal = ({
  open,
  setOpen,
  comment,
}: CommunityCommentModalProps) => {
  if (!comment) {
    return null;
  }

  return (
    <ColumnModal
      cols={4}
      open={open}
      onOpenChange={setOpen}
      hideTrigger
      hideFooter
      closeButtonClassName="top-[36px]"
      maxHeight="85vh"
    >
      <div className="flex flex-col px-6">
        <CommunityPostHeader className="mb-[20px] ml-0" />
        <EditCommentInput
          comment={comment}
          onCancel={() => setOpen(false)}
          onSuccess={() => setOpen(false)}
        />
      </div>
    </ColumnModal>
  );
};
